import { useState, useEffect } from 'react'
import { Tooltip } from '../../../components/ui/Tooltip'
import {
  Activity,
  TrendingUp,
  Clock,
  ExternalLink,
  Linkedin,
  Globe,
  Users,
  X,
  Plus,
  Trash2,
  HelpCircle,
  User,
  Link2,
  CheckCircle2,
  AlertCircle,
  Play,
  Search,
  Tag,
  Loader2,
  Calendar,
} from 'lucide-react'
import { clsx } from 'clsx'
import { getAuthHeaders } from '@api/client'

interface Signal {
  id: string
  type: string
  category: string
  title: string
  description: string | null
  signal_date: string
  source_url: string | null
}

interface IntentCompany {
  id: string
  name: string
  domain: string | null
  intent_score: number
  composite_score: number
  linkedin_url: string | null
  signal_count: number
  signals: Signal[]
}

interface TrackedWebsite {
  id: string
  url: string
  addedAt: string
}

interface TrackedKeyword {
  id: string
  keyword: string
  addedAt: string
}

interface TrackedContact {
  id: string
  name: string
  linkedinUrl?: string
  email?: string
  title?: string
  isFromHubSpot: boolean
}

interface TrackingConfig {
  websites: TrackedWebsite[]
  keywords: TrackedKeyword[]
  contacts: TrackedContact[]
  lastRun: string | null
  lastRunStatus: 'success' | 'error' | 'running' | null
  lastRunResults: {
    signalsFound: number
    websitesScanned: number
    contactsChecked: number
  } | null
}

interface HubSpotContact {
  id: string
  properties: {
    firstname?: string
    lastname?: string
    email?: string
    jobtitle?: string
    linkedin_url?: string
    hs_linkedin_url?: string
  }
}

interface SignalTrackerProps {
  accountId: string
  accountName: string
  domain: string | null
  onClose: () => void
}

// Category color mapping
const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  linkedin_activity: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
  },
  company_news: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800',
  },
  hiring: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
  },
  technology: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
  },
  engagement: {
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    text: 'text-pink-700 dark:text-pink-300',
    border: 'border-pink-200 dark:border-pink-800',
  },
  default: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-700 dark:text-slate-300',
    border: 'border-slate-200 dark:border-slate-700',
  },
}

// Default keywords to suggest
const SUGGESTED_KEYWORDS = [
  'partnership',
  'funding',
  'acquisition',
  'expansion',
  'hiring',
  'new product',
  'digital transformation',
  'compliance',
  'regulation',
  'growth',
]

function getCategoryColor(category: string) {
  return categoryColors[category] || categoryColors.default
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor(diffMs / (1000 * 60))

  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes} min ago`
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return `${Math.floor(diffDays / 365)} years ago`
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-500'
  if (score >= 60) return 'text-blue-500'
  if (score >= 40) return 'text-amber-500'
  return 'text-slate-400'
}

function ScoreRing({ score }: { score: number }) {
  const circumference = 2 * Math.PI * 36
  const strokeDashoffset = circumference - (score / 100) * circumference
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#94a3b8'

  return (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24 -rotate-90">
        <circle
          cx="48"
          cy="48"
          r="36"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-[var(--bg-tertiary)]"
        />
        <circle
          cx="48"
          cy="48"
          r="36"
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={clsx('text-2xl font-bold', getScoreColor(score))}>{score}</span>
      </div>
    </div>
  )
}

// Section header with tooltip
function SectionHeader({
  icon: Icon,
  title,
  tooltip,
  count,
  action,
}: {
  icon: React.ElementType
  title: string
  tooltip: string
  count?: number
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-[var(--text-muted)]" />
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          {title}
          {count !== undefined && (
            <span className="ml-1 text-[var(--text-muted)] font-normal">({count})</span>
          )}
        </h3>
        <Tooltip content={tooltip}>
          <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
        </Tooltip>
      </div>
      {action}
    </div>
  )
}

// Tab component
function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
        active
          ? 'bg-primary text-white'
          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
      )}
    >
      {children}
    </button>
  )
}

export function SignalTracker({ accountId, accountName, domain, onClose }: SignalTrackerProps) {
  const [activeTab, setActiveTab] = useState<'signals' | 'tracking'>('signals')
  const [company, setCompany] = useState<IntentCompany | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Tracking config state
  const [trackingConfig, setTrackingConfig] = useState<TrackingConfig>({
    websites: [],
    keywords: [],
    contacts: [],
    lastRun: null,
    lastRunStatus: null,
    lastRunResults: null,
  })

  // Form states
  const [newWebsiteUrl, setNewWebsiteUrl] = useState('')
  const [websiteError, setWebsiteError] = useState<string | null>(null)
  const [newKeyword, setNewKeyword] = useState('')
  const [newContactName, setNewContactName] = useState('')
  const [newContactLinkedIn, setNewContactLinkedIn] = useState('')
  const [showAddContact, setShowAddContact] = useState(false)
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [isRunningAutomation, setIsRunningAutomation] = useState(false)

  // Track if we have unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  // Load tracking config from API
  useEffect(() => {
    async function loadConfig() {
      setIsLoadingConfig(true)
      try {
        const response = await fetch(`/api/signal-tracking/configs/${accountId}`, {
          headers: getAuthHeaders(),
        })
        if (response.ok) {
          const data = await response.json()
          if (data.config) {
            // Transform API response to component format
            const config = data.config
            setTrackingConfig({
              websites: (config.websites || []).map((w: { id: number; url: string; added_at: string }) => ({
                id: `web-${w.id}`,
                url: w.url,
                addedAt: w.added_at,
              })),
              keywords: (config.keywords || []).map((k: { id: number; keyword: string; added_at: string }) => ({
                id: `kw-${k.id}`,
                keyword: k.keyword,
                addedAt: k.added_at,
              })),
              contacts: (config.contacts || []).map((c: { id: number; name: string; email?: string; title?: string; linkedin_url?: string; is_from_hubspot: number; hubspot_contact_id?: string }) => ({
                id: c.is_from_hubspot ? `hs-${c.hubspot_contact_id || c.id}` : `manual-${c.id}`,
                name: c.name,
                email: c.email || undefined,
                title: c.title || undefined,
                linkedinUrl: c.linkedin_url || undefined,
                isFromHubSpot: c.is_from_hubspot === 1,
              })),
              lastRun: config.last_run_at,
              lastRunStatus: config.last_run_status,
              lastRunResults: config.last_run_signals_found ? {
                signalsFound: config.last_run_signals_found,
                websitesScanned: (config.websites || []).length,
                contactsChecked: (config.contacts || []).length,
              } : null,
            })
          }
        }
      } catch (err) {
        console.error('Failed to load tracking config:', err)
      } finally {
        setIsLoadingConfig(false)
      }
    }

    loadConfig()
  }, [accountId])

  // Save tracking config to API (debounced)
  useEffect(() => {
    if (!hasUnsavedChanges || isLoadingConfig) return

    const saveTimeout = setTimeout(async () => {
      setIsSaving(true)
      try {
        // Transform component format to API format
        const payload = {
          company_id: accountId,
          company_name: accountName,
          company_domain: domain,
          websites: trackingConfig.websites.map((w) => ({ url: w.url })),
          keywords: trackingConfig.keywords.map((k) => ({ keyword: k.keyword })),
          contacts: trackingConfig.contacts.map((c) => ({
            name: c.name,
            email: c.email,
            title: c.title,
            linkedin_url: c.linkedinUrl,
            is_from_hubspot: c.isFromHubSpot,
            hubspot_contact_id: c.isFromHubSpot ? c.id.replace('hs-', '') : undefined,
          })),
        }

        const response = await fetch('/api/signal-tracking/configs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(payload),
        })

        if (response.ok) {
          setHasUnsavedChanges(false)
        } else {
          console.error('Failed to save tracking config:', response.status)
        }
      } catch (err) {
        console.error('Failed to save tracking config:', err)
      } finally {
        setIsSaving(false)
      }
    }, 1000) // Debounce for 1 second

    return () => clearTimeout(saveTimeout)
  }, [trackingConfig, hasUnsavedChanges, isLoadingConfig, accountId, accountName, domain])

  // Fetch signals from Intent Based Outbound API
  useEffect(() => {
    async function fetchSignals() {
      if (!domain) {
        setError('No domain available for this account')
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(
          `https://intent-outbound-api.pmgoncalves0.workers.dev/api/v1/companies?search=${encodeURIComponent(domain)}&limit=1`
        )

        if (!response.ok) {
          throw new Error('Failed to fetch signals')
        }

        const data = await response.json()

        if (data.companies && data.companies.length > 0) {
          const companyId = data.companies[0].id
          const detailResponse = await fetch(
            `https://intent-outbound-api.pmgoncalves0.workers.dev/api/v1/companies/${companyId}`
          )

          if (detailResponse.ok) {
            const detailData = await detailResponse.json()
            setCompany(detailData.company)
          } else {
            setCompany(data.companies[0])
          }
        } else {
          setCompany(null)
        }
      } catch (err) {
        console.error('Signal fetch error:', err)
        setError(err instanceof Error ? err.message : 'Failed to load signals')
      } finally {
        setIsLoading(false)
      }
    }

    fetchSignals()
  }, [domain])

  // Fetch associated contacts from HubSpot
  useEffect(() => {
    async function fetchHubSpotContacts() {
      if (!accountId) return

      setIsLoadingContacts(true)
      try {
        const response = await fetch(`/api/companies/${accountId}/contacts`, {
          headers: getAuthHeaders(),
        })

        if (response.ok) {
          const data = await response.json()
          const hubspotContacts: TrackedContact[] = (data.contacts || []).map(
            (contact: HubSpotContact) => ({
              id: `hs-${contact.id}`,
              name: [contact.properties.firstname, contact.properties.lastname]
                .filter(Boolean)
                .join(' ') || 'Unknown',
              email: contact.properties.email || undefined,
              title: contact.properties.jobtitle || undefined,
              linkedinUrl:
                contact.properties.linkedin_url ||
                contact.properties.hs_linkedin_url ||
                undefined,
              isFromHubSpot: true,
            })
          )

          // Merge with existing manual contacts
          setTrackingConfig((prev) => {
            const manualContacts = prev.contacts.filter((c) => !c.isFromHubSpot)
            return {
              ...prev,
              contacts: [...hubspotContacts, ...manualContacts],
            }
          })
        }
      } catch (err) {
        console.error('Failed to fetch HubSpot contacts:', err)
      } finally {
        setIsLoadingContacts(false)
      }
    }

    fetchHubSpotContacts()
  }, [accountId])

  // Add website
  const handleAddWebsite = () => {
    setWebsiteError(null)

    let url = newWebsiteUrl.trim()
    if (!url) return

    // Add https:// if not present
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`
    }

    // Validate URL
    try {
      new URL(url)
    } catch {
      setWebsiteError('Please enter a valid URL')
      return
    }

    // Check for duplicates
    if (trackingConfig.websites.some((w) => w.url === url)) {
      setWebsiteError('This website is already being tracked')
      return
    }

    const newWebsite: TrackedWebsite = {
      id: `web-${Date.now()}`,
      url,
      addedAt: new Date().toISOString(),
    }

    setTrackingConfig((prev) => ({
      ...prev,
      websites: [...prev.websites, newWebsite],
    }))
    setNewWebsiteUrl('')
    setHasUnsavedChanges(true)
  }

  // Remove website
  const handleRemoveWebsite = (id: string) => {
    setTrackingConfig((prev) => ({
      ...prev,
      websites: prev.websites.filter((w) => w.id !== id),
    }))
    setHasUnsavedChanges(true)
  }

  // Add keyword
  const handleAddKeyword = () => {
    const keyword = newKeyword.trim().toLowerCase()
    if (!keyword) return

    // Check for duplicates
    if (trackingConfig.keywords.some((k) => k.keyword.toLowerCase() === keyword)) {
      return
    }

    const newKw: TrackedKeyword = {
      id: `kw-${Date.now()}`,
      keyword,
      addedAt: new Date().toISOString(),
    }

    setTrackingConfig((prev) => ({
      ...prev,
      keywords: [...prev.keywords, newKw],
    }))
    setNewKeyword('')
    setHasUnsavedChanges(true)
  }

  // Remove keyword
  const handleRemoveKeyword = (id: string) => {
    setTrackingConfig((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k.id !== id),
    }))
    setHasUnsavedChanges(true)
  }

  // Add suggested keyword
  const handleAddSuggestedKeyword = (keyword: string) => {
    if (trackingConfig.keywords.some((k) => k.keyword.toLowerCase() === keyword.toLowerCase())) {
      return
    }

    const newKw: TrackedKeyword = {
      id: `kw-${Date.now()}`,
      keyword,
      addedAt: new Date().toISOString(),
    }

    setTrackingConfig((prev) => ({
      ...prev,
      keywords: [...prev.keywords, newKw],
    }))
    setHasUnsavedChanges(true)
  }

  // Add contact
  const handleAddContact = () => {
    const name = newContactName.trim()
    if (!name) return

    const newContact: TrackedContact = {
      id: `manual-${Date.now()}`,
      name,
      linkedinUrl: newContactLinkedIn.trim() || undefined,
      isFromHubSpot: false,
    }

    setTrackingConfig((prev) => ({
      ...prev,
      contacts: [...prev.contacts, newContact],
    }))
    setNewContactName('')
    setNewContactLinkedIn('')
    setShowAddContact(false)
    setHasUnsavedChanges(true)
  }

  // Remove contact
  const handleRemoveContact = (id: string) => {
    setTrackingConfig((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((c) => c.id !== id),
    }))
    setHasUnsavedChanges(true)
  }

  // Run automation
  const handleRunAutomation = async () => {
    setIsRunningAutomation(true)
    setTrackingConfig((prev) => ({
      ...prev,
      lastRunStatus: 'running',
    }))

    try {
      // Call the tracking API endpoint
      const response = await fetch(`/api/signal-tracking/run/${accountId}?type=manual`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to run tracking')
      }

      const data = await response.json()

      const results = {
        signalsFound: data.signals_found || 0,
        websitesScanned: trackingConfig.websites.length,
        contactsChecked: trackingConfig.contacts.length,
      }

      setTrackingConfig((prev) => ({
        ...prev,
        lastRun: new Date().toISOString(),
        lastRunStatus: 'success',
        lastRunResults: results,
      }))

      // Switch to signals tab to show results
      if (results.signalsFound > 0) {
        setActiveTab('signals')
      }
    } catch (err) {
      console.error('Automation error:', err)
      setTrackingConfig((prev) => ({
        ...prev,
        lastRunStatus: 'error',
      }))
    } finally {
      setIsRunningAutomation(false)
    }
  }

  const hubspotContacts = trackingConfig.contacts.filter((c) => c.isFromHubSpot)
  const manualContacts = trackingConfig.contacts.filter((c) => !c.isFromHubSpot)

  // Check if tracking is configured
  const isTrackingConfigured =
    trackingConfig.websites.length > 0 ||
    trackingConfig.keywords.length > 0 ||
    trackingConfig.contacts.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Activity size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                Intent Signal Tracking
              </h2>
              <div className="flex items-center gap-2">
                <p className="text-sm text-[var(--text-muted)]">{accountName}</p>
                {isSaving && (
                  <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
                    <Loader2 size={10} className="animate-spin" />
                    Saving...
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between gap-2 p-4 border-b border-[var(--border-color)]">
          <div className="flex gap-2">
            <Tab active={activeTab === 'signals'} onClick={() => setActiveTab('signals')}>
              <div className="flex items-center gap-2">
                <Activity size={16} />
                Signals
              </div>
            </Tab>
            <Tab active={activeTab === 'tracking'} onClick={() => setActiveTab('tracking')}>
              <div className="flex items-center gap-2">
                <Users size={16} />
                Tracking Settings
              </div>
            </Tab>
          </div>

          {/* Run Automation Button */}
          {activeTab === 'tracking' && isTrackingConfigured && (
            <button
              onClick={handleRunAutomation}
              disabled={isRunningAutomation}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                isRunningAutomation
                  ? 'bg-primary/50 text-white cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary/90'
              )}
            >
              {isRunningAutomation ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Run Now
                </>
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'signals' ? (
            // Signals Tab
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <Activity size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                  <p className="text-[var(--text-muted)]">{error}</p>
                </div>
              ) : !company ? (
                <div className="text-center py-12">
                  <Activity size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                  <p className="text-[var(--text-primary)] font-medium mb-2">
                    No signals tracked yet
                  </p>
                  <p className="text-sm text-[var(--text-muted)]">
                    This company is not currently being monitored. Go to Tracking Settings to
                    configure what to track.
                  </p>
                  {domain && (
                    <p className="text-xs text-[var(--text-muted)] mt-2">Domain: {domain}</p>
                  )}
                  <button
                    onClick={() => setActiveTab('tracking')}
                    className="mt-4 px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Configure Tracking
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Score Overview */}
                  <div className="flex items-center justify-between bg-[var(--bg-tertiary)] rounded-lg p-4">
                    <div className="flex items-center gap-6">
                      <ScoreRing score={company.composite_score || 0} />
                      <div>
                        <div className="text-sm text-[var(--text-muted)] mb-1">
                          Composite Score
                        </div>
                        <div className="text-2xl font-bold text-[var(--text-primary)]">
                          {company.composite_score || 0}
                          <span className="text-sm font-normal text-[var(--text-muted)]">
                            /100
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <div className="flex items-center gap-1">
                            <TrendingUp size={14} className="text-blue-500" />
                            <span className="text-[var(--text-secondary)]">
                              Intent: {company.intent_score || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Activity size={14} className="text-purple-500" />
                            <span className="text-[var(--text-secondary)]">
                              {company.signal_count || company.signals?.length || 0} signals
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      {company.linkedin_url && (
                        <a
                          href={company.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--bg-secondary)] rounded-lg text-[var(--text-secondary)] hover:text-primary transition-colors"
                        >
                          <Linkedin size={16} />
                          LinkedIn
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {domain && (
                        <a
                          href={`https://${domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--bg-secondary)] rounded-lg text-[var(--text-secondary)] hover:text-primary transition-colors"
                        >
                          <Globe size={16} />
                          Website
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Signals List */}
                  {company.signals && company.signals.length > 0 ? (
                    <div>
                      <h3 className="text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-3">
                        Recent Signals ({company.signals.length})
                      </h3>
                      <div className="space-y-3">
                        {company.signals.map((signal) => {
                          const colors = getCategoryColor(signal.category)
                          return (
                            <div
                              key={signal.id}
                              className={clsx(
                                'border rounded-lg p-3 transition-colors',
                                colors.border,
                                'hover:bg-[var(--bg-tertiary)]'
                              )}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span
                                      className={clsx(
                                        'text-xs font-medium px-2 py-0.5 rounded',
                                        colors.bg,
                                        colors.text
                                      )}
                                    >
                                      {signal.category.replace(/_/g, ' ')}
                                    </span>
                                    <span className="text-xs text-[var(--text-muted)]">
                                      {signal.type}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-2">
                                    {signal.title}
                                  </p>
                                  {signal.description && (
                                    <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">
                                      {signal.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                  <span className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                                    <Clock size={12} />
                                    {formatRelativeDate(signal.signal_date)}
                                  </span>
                                  {signal.source_url && (
                                    <a
                                      href={signal.source_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary hover:underline flex items-center gap-1"
                                    >
                                      View source
                                      <ExternalLink size={10} />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[var(--text-muted)]">
                      <Users size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No signals recorded for this company yet</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            // Tracking Settings Tab
            <div className="space-y-6">
              {/* Last Run Status */}
              {trackingConfig.lastRun && (
                <div
                  className={clsx(
                    'rounded-lg p-3 border',
                    trackingConfig.lastRunStatus === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : trackingConfig.lastRunStatus === 'error'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {trackingConfig.lastRunStatus === 'success' ? (
                        <CheckCircle2 size={16} className="text-green-500" />
                      ) : trackingConfig.lastRunStatus === 'error' ? (
                        <AlertCircle size={16} className="text-red-500" />
                      ) : (
                        <Loader2 size={16} className="text-blue-500 animate-spin" />
                      )}
                      <span
                        className={clsx(
                          'text-sm font-medium',
                          trackingConfig.lastRunStatus === 'success'
                            ? 'text-green-700 dark:text-green-300'
                            : trackingConfig.lastRunStatus === 'error'
                              ? 'text-red-700 dark:text-red-300'
                              : 'text-blue-700 dark:text-blue-300'
                        )}
                      >
                        {trackingConfig.lastRunStatus === 'running'
                          ? 'Automation running...'
                          : `Last run: ${formatRelativeDate(trackingConfig.lastRun)}`}
                      </span>
                    </div>
                    {trackingConfig.lastRunResults && trackingConfig.lastRunStatus === 'success' && (
                      <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
                        <span>{trackingConfig.lastRunResults.signalsFound} signals found</span>
                        <span>•</span>
                        <span>{trackingConfig.lastRunResults.websitesScanned} sites scanned</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Keywords Section */}
              <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
                <SectionHeader
                  icon={Search}
                  title="Keywords to Monitor"
                  tooltip="Add keywords or phrases to search for across tracked websites and LinkedIn activity. When these terms appear in articles, posts, or updates, we'll create a signal."
                  count={trackingConfig.keywords.length}
                />

                {/* Existing keywords */}
                {trackingConfig.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {trackingConfig.keywords.map((kw) => (
                      <span
                        key={kw.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--bg-secondary)] rounded-full text-sm text-[var(--text-secondary)]"
                      >
                        <Tag size={12} />
                        {kw.keyword}
                        <button
                          onClick={() => handleRemoveKeyword(kw.id)}
                          className="ml-1 p-0.5 hover:text-red-500 transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add keyword form */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                    placeholder="Add a keyword (e.g., digital transformation)"
                    className="flex-1 px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary"
                  />
                  <button
                    onClick={handleAddKeyword}
                    disabled={!newKeyword.trim()}
                    className="px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>

                {/* Suggested keywords */}
                <div>
                  <p className="text-xs text-[var(--text-muted)] mb-2">Suggested keywords:</p>
                  <div className="flex flex-wrap gap-1">
                    {SUGGESTED_KEYWORDS.filter(
                      (kw) =>
                        !trackingConfig.keywords.some(
                          (k) => k.keyword.toLowerCase() === kw.toLowerCase()
                        )
                    ).map((kw) => (
                      <button
                        key={kw}
                        onClick={() => handleAddSuggestedKeyword(kw)}
                        className="px-2 py-1 text-xs text-[var(--text-muted)] hover:text-primary border border-dashed border-[var(--border-color)] hover:border-primary rounded-full transition-colors"
                      >
                        + {kw}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Websites Section */}
              <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
                <SectionHeader
                  icon={Link2}
                  title="Tracked Websites"
                  tooltip="Add websites to monitor for news, updates, or mentions of the company and your keywords. This could include industry publications, news sites, or partner websites."
                  count={trackingConfig.websites.length}
                />

                {/* Existing websites */}
                {trackingConfig.websites.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {trackingConfig.websites.map((website) => (
                      <div
                        key={website.id}
                        className="flex items-center justify-between bg-[var(--bg-secondary)] rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Globe size={14} className="text-[var(--text-muted)] flex-shrink-0" />
                          <a
                            href={website.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[var(--text-primary)] hover:text-primary truncate"
                          >
                            {website.url.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                        <button
                          onClick={() => handleRemoveWebsite(website.id)}
                          className="p-1 text-[var(--text-muted)] hover:text-red-500 transition-colors flex-shrink-0"
                          title="Remove website"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add website form */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newWebsiteUrl}
                    onChange={(e) => {
                      setNewWebsiteUrl(e.target.value)
                      setWebsiteError(null)
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddWebsite()}
                    placeholder="Enter website URL (e.g., techcrunch.com)"
                    className="flex-1 px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary"
                  />
                  <button
                    onClick={handleAddWebsite}
                    disabled={!newWebsiteUrl.trim()}
                    className="px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>
                {websiteError && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {websiteError}
                  </p>
                )}
              </div>

              {/* LinkedIn Contacts Section */}
              <div className="bg-[var(--bg-tertiary)] rounded-lg p-4">
                <SectionHeader
                  icon={Linkedin}
                  title="LinkedIn Activity Tracking"
                  tooltip="Track LinkedIn activity for key contacts at this company. We'll monitor their posts, comments, and engagement to identify buying signals and conversation starters."
                  count={trackingConfig.contacts.length}
                  action={
                    <button
                      onClick={() => setShowAddContact(!showAddContact)}
                      className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                    >
                      <Plus size={12} />
                      Add Contact
                    </button>
                  }
                />

                {/* Add contact form */}
                {showAddContact && (
                  <div className="bg-[var(--bg-secondary)] rounded-lg p-3 mb-3 space-y-2">
                    <input
                      type="text"
                      value={newContactName}
                      onChange={(e) => setNewContactName(e.target.value)}
                      placeholder="Contact name (e.g., John Smith)"
                      className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      value={newContactLinkedIn}
                      onChange={(e) => setNewContactLinkedIn(e.target.value)}
                      placeholder="LinkedIn URL (optional)"
                      className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => {
                          setShowAddContact(false)
                          setNewContactName('')
                          setNewContactLinkedIn('')
                        }}
                        className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddContact}
                        disabled={!newContactName.trim()}
                        className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Contact
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading state */}
                {isLoadingContacts && (
                  <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    Loading contacts from HubSpot...
                  </div>
                )}

                {/* HubSpot contacts */}
                {hubspotContacts.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 size={12} className="text-green-500" />
                      <span className="text-xs font-medium text-[var(--text-muted)]">
                        From HubSpot ({hubspotContacts.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {hubspotContacts.map((contact) => (
                        <ContactCard key={contact.id} contact={contact} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual contacts */}
                {manualContacts.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <User size={12} className="text-blue-500" />
                      <span className="text-xs font-medium text-[var(--text-muted)]">
                        Manually Added ({manualContacts.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {manualContacts.map((contact) => (
                        <ContactCard
                          key={contact.id}
                          contact={contact}
                          onRemove={() => handleRemoveContact(contact.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!isLoadingContacts && trackingConfig.contacts.length === 0 && (
                  <div className="text-center py-6">
                    <Users size={32} className="mx-auto text-[var(--text-muted)] mb-2 opacity-50" />
                    <p className="text-sm text-[var(--text-muted)]">No contacts to track yet</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Add contacts manually or they'll be imported from HubSpot associations
                    </p>
                  </div>
                )}
              </div>

              {/* Info box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex gap-2">
                  <HelpCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">How tracking works</p>
                    <p className="mb-2">
                      Click <strong>"Run Now"</strong> to manually trigger the automation, or set up
                      a schedule to run it automatically (e.g., daily at 9am).
                    </p>
                    <p>
                      The automation will scan tracked websites for your keywords and check LinkedIn
                      activity for your contacts. Any relevant mentions or activity will be captured
                      as intent signals.
                    </p>
                  </div>
                </div>
              </div>

              {/* Schedule info (future feature) */}
              <div className="bg-[var(--bg-tertiary)] rounded-lg p-3 border border-dashed border-[var(--border-color)]">
                <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                  <Calendar size={16} />
                  <span>
                    <strong>Coming soon:</strong> Schedule automated runs (daily, weekly) via cron
                    job
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--border-color)] bg-[var(--bg-tertiary)]">
          <p className="text-xs text-[var(--text-muted)] text-center">
            Signals powered by Intent Based Outbound • Data refreshes on demand or via scheduled
            automation
          </p>
        </div>
      </div>
    </div>
  )
}

// Contact card component
function ContactCard({
  contact,
  onRemove,
}: {
  contact: TrackedContact
  onRemove?: () => void
}) {
  return (
    <div className="flex items-center justify-between bg-[var(--bg-secondary)] rounded-lg px-3 py-2">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center flex-shrink-0">
          <User size={14} className="text-[var(--text-muted)]" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-[var(--text-primary)] truncate">
            {contact.name}
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            {contact.title && <span className="truncate">{contact.title}</span>}
            {contact.linkedinUrl && (
              <a
                href={contact.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-600 flex items-center gap-0.5"
              >
                <Linkedin size={10} />
                Profile
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {contact.isFromHubSpot && (
          <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
            HubSpot
          </span>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="p-1 text-[var(--text-muted)] hover:text-red-500 transition-colors"
            title="Remove contact"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  )
}
